import { useEffect, useCallback } from "react";
import { getAccessToken } from "@/api/client";
import { authApi, brandingApi } from "@/api/services";
import { displayNameOf, useAuthStore } from "@/stores/auth";
import { useThemeStore } from "@/stores/theme";

/**
 * Silently loads the current user from the API on app start
 * if an access token exists in storage. Also fetches the org's
 * color palette and applies it.
 *
 * Listens to browser back/forward (popstate) events to restore
 * auth state when the in-memory Zustand store has been cleared
 * but the JWT token still lives in localStorage.
 */
export function AuthInitializer() {
  const setUser = useAuthStore((s) => s.setUser);
  const setPalette = useThemeStore((s) => s.setPalette);

  const loadUser = useCallback(() => {
    const token = getAccessToken();
    if (!token) return;

    // Skip ONLY if the stored user is COMPLETE — i.e. already carries a real
    // name. A user persisted by an older build (or before display_name was
    // wired) can be in localStorage with an empty/placeholder `name`; if we
    // skipped purely on presence, that stale record would stick forever and
    // the greeting would render name-less ("good morning, …" with no name).
    // Re-fetch /auth/me whenever the name is missing or the generic "User"
    // placeholder, so the hero greeting always reflects who's logged in.
    const existing = useAuthStore.getState().user;
    if (existing && existing.name && existing.name.trim() && existing.name !== "User") {
      return;
    }

    authApi
      .me()
      .then((data) => {
        const extended = data as unknown as { display_name?: string; email?: string; id: string; org_id: string; roles?: string[]; is_platform_admin?: boolean };
        const email = extended.email || "";
        const name = displayNameOf(extended);
        setUser({
          id: extended.id,
          name,
          email,
          roles: extended.roles ?? [],
          org_id: extended.org_id,
          org_name: "",
          is_platform_admin: extended.is_platform_admin ?? false,
        });

        // Fetch org palette and apply (non-blocking)
        brandingApi.getPalette().then((res) => {
          if (res.palette_id) {
            setPalette(res.palette_id);
          }
        }).catch(() => {
          // Silently fail — localStorage palette is already applied
        });
      })
      .catch(() => {
        // Token is invalid/expired - silent fail, ProtectedRoute will redirect
      });
  }, [setUser, setPalette]);

  // Load user on mount. Always call loadUser — its own completeness check
  // (token present + stored user lacks a real name) decides whether to fetch,
  // so a stale name-less persisted user gets reconciled instead of sticking.
  useEffect(() => {
    loadUser();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-validate auth on browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      loadUser();
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [loadUser]);

  return null;
}
